/**
 * React Hook for Supabase Table Metadata
 * Provides real-time table information for the SmartSecondarySidebar
 */

import { useState, useEffect, useCallback } from 'react';
import { getVRPSystemTables, type TableWithRowCount } from '../utils/supabaseTableMetadata';

export interface UseSupabaseTableMetadataResult {
  tables: TableWithRowCount[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

export function useSupabaseTableMetadata(schema: string = 'public'): UseSupabaseTableMetadataResult {
  const [tables, setTables] = useState<TableWithRowCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const tableData = await getVRPSystemTables(schema);
      setTables(tableData);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch table metadata';
      
      // More specific error handling for better user experience
      if (errorMessage.includes('Authentication required')) {
        setError('Please log in to view table metadata');
      } else if (errorMessage.includes('RLS') || errorMessage.includes('400')) {
        setError('Some tables are not accessible due to permissions. Showing available tables.');
        // Don't completely fail - show what we can access
        console.warn('Partial table access due to RLS policies');
      } else if (errorMessage.includes('get_table_metadata')) {
        setError('Database function missing. Using fallback method.');
        console.warn('RPC function not available, using fallback');
      } else {
        setError(errorMessage);
      }
      
      console.error('Error fetching table metadata:', err);
    } finally {
      setLoading(false);
    }
  }, [schema]);

  const refresh = useCallback(async () => {
    await fetchTables();
  }, [fetchTables]);

  // Initial load
  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTables();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [fetchTables]);

  return {
    tables,
    loading,
    error,
    refresh,
    lastUpdated
  };
}